/**
 * Hover-open header dropdowns (desktop), with navigable parents.
 *
 * The theme's dropdowns are native <details> elements that open on click -
 * details-disclosure.js binds only `toggle` and `focusout`. This adds
 * open-on-hover so the nav behaves like the intended design, without removing
 * the click or keyboard paths: toggling the `open` attribute fires the same
 * `toggle` event HeaderMenu already listens for.
 *
 * Handing the panel to hover frees the parent's click to navigate, which a
 * <summary> otherwise cannot do - see onSummaryActivate below.
 *
 * Only runs on a real pointer at desktop widths, so touch and the mobile
 * drawer keep click-to-open.
 */
(function () {
  if (window.headerHoverMenus) return;
  window.headerHoverMenus = true;

  const DESKTOP = '(min-width: 990px) and (hover: hover)';
  const CLOSE_DELAY = 150;

  let closeTimer;

  function isDesktop() {
    return window.matchMedia(DESKTOP).matches;
  }

  function allMenus() {
    return document.querySelectorAll('.header__inline-menu header-menu');
  }

  function openMenu(menu) {
    const details = menu.querySelector('details');
    if (!details || details.open) return;
    details.setAttribute('open', '');
    const summary = details.querySelector('summary');
    if (summary) summary.setAttribute('aria-expanded', 'true');
  }

  function closeMenu(menu) {
    const details = menu.querySelector('details');
    if (!details || !details.open) return;
    // HeaderMenu.close() also resets aria-expanded; use it when it is upgraded.
    if (typeof menu.close === 'function') {
      menu.close();
    } else {
      details.removeAttribute('open');
    }
  }

  /** Opens `menu` (if given) and closes every other one. */
  function openOnly(menu) {
    allMenus().forEach(function (other) {
      if (other !== menu) closeMenu(other);
    });
    if (menu) openMenu(menu);
  }

  function scheduleCloseAll() {
    clearTimeout(closeTimer);
    closeTimer = setTimeout(function () {
      openOnly(null);
    }, CLOSE_DELAY);
  }

  document.addEventListener('mouseover', function (event) {
    if (!isDesktop() || !event.target.closest) return;

    const nav = event.target.closest('.header__inline-menu');
    if (!nav) {
      scheduleCloseAll();
      return;
    }

    clearTimeout(closeTimer);
    // Hovering a plain top-level link closes whatever was open.
    openOnly(event.target.closest('header-menu'));
  });

  // Pointer left the page entirely - no further mouseover will arrive.
  document.documentElement.addEventListener('mouseleave', function () {
    if (isDesktop()) scheduleCloseAll();
  });

  /*
   * Dropdown parents are <summary> elements, which carry no href - clicking one
   * only toggled its panel, so the parent page ("Education", "Engagement Rings")
   * had no way in. The panel already opens on hover here, which frees the click
   * to navigate instead. The snippets pass the destination as data-link-url.
   *
   * Gated on the same desktop-with-hover query as the rest of this file: where
   * the panel opens on tap, the tap has to stay the only way to open it.
   */

  /** The nav summary an event landed on, if it has somewhere to navigate to. */
  function linkedSummary(event) {
    if (!event.target.closest) return null;
    const summary = event.target.closest('summary[data-link-url]');
    return summary && summary.closest('.header__inline-menu') ? summary : null;
  }

  function onSummaryActivate(event) {
    if (!isDesktop()) return;

    // Middle click arrives as auxclick; every other button is not an activation.
    const middleClick = event.type === 'auxclick' && event.button === 1;
    if (!middleClick && event.button !== 0) return;

    const summary = linkedSummary(event);
    if (!summary) return;

    // Suppress the toggle either way - the panel is the hover's job now.
    event.preventDefault();

    const url = summary.dataset.linkUrl;
    if (middleClick || event.metaKey || event.ctrlKey || event.shiftKey) {
      window.open(url, '_blank', 'noopener');
    } else {
      window.location.href = url;
    }
  }

  document.addEventListener('click', onSummaryActivate);
  document.addEventListener('auxclick', onSummaryActivate);

  /*
   * Keyboard keeps both paths, the way a link with a submenu is expected to
   * behave: Enter follows the link (it arrives as a click above), while Space
   * and ArrowDown open the panel. Preventing the default on Space matters - it
   * would otherwise toggle the details and then fire a click that navigates.
   */
  document.addEventListener('keydown', function (event) {
    if (event.key === 'Escape') {
      const open = event.target.closest && event.target.closest('.header__inline-menu header-menu');
      if (!open) return;
      const summary = open.querySelector('summary');
      closeMenu(open);
      if (summary) summary.focus();
      return;
    }

    if (event.key !== ' ' && event.key !== 'ArrowDown') return;

    const summary = linkedSummary(event);
    if (!summary) return;

    event.preventDefault();
    openOnly(summary.closest('header-menu'));

    if (event.key === 'ArrowDown') {
      const panel = summary.parentElement.querySelector('.header__submenu');
      const first = panel && panel.querySelector('a, summary');
      if (first) first.focus();
    }
  });
})();
